import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scissors } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/PageHeader";

const FABRIC_PHOTOS = [
  { label: "Getzner", src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_fabric-depot-system/artifacts/6198cb975db7b8c3_WhatsApp-Image-2021-04-26-at-14.49.40-thumb-large.jpg" },
  { label: "Bazin riche", src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_fabric-depot-system/artifacts/48a56c5072bf71e8_thumb_4784850_uploaded_image1_1717774584.jpg" },
  { label: "Brodé", src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_fabric-depot-system/artifacts/61aa92770cf649df_brode.webp" },
  { label: "Super 100", src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_fabric-depot-system/artifacts/09b6988ea20111bf_images.jfif" },
  { label: "Voile", src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_fabric-depot-system/artifacts/4bb9fb0ef7398c10_tissus-suisse-voile-dentelle-en-suisse-dubai-tissu.webp" },
  { label: "Wax", src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_fabric-depot-system/artifacts/cfd4fc60d412278a_tissu-wax-africain_c66fa7d9-7313-41cb-8a78-b54358e13a5a.webp" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await login(email, password); nav("/"); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#ccfbf1] p-12 text-foreground lg:flex">
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-1 opacity-90" aria-hidden="true" data-testid="login-fabric-gallery">
          {FABRIC_PHOTOS.map((p) => <figure key={p.label} className="relative m-0 overflow-hidden"><img src={p.src} alt={p.label} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" /><figcaption className="absolute bottom-2 left-2 rounded bg-black/55 px-2 py-0.5 text-xs font-semibold text-white">{p.label}</figcaption></figure>)}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#042f2e]/90 via-[#042f2e]/40 to-transparent" aria-hidden="true" />
        <div className="relative flex items-center gap-3 text-white"><div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#059669]"><Scissors /></div><span className="font-display text-2xl font-semibold">Touba Tissu</span></div>
        <div className="relative text-white">
          <h2 className="font-display text-5xl font-semibold leading-tight">Chaque mètre compté,<br />chaque dépôt maîtrisé.</h2>
          <p className="mt-4 max-w-md text-white/80">Getzner, Bazin riche, brodé, Super 100, voile… gestion des ventes de tissus multi-utilisateurs avec stock séparé par dépôt.</p>
        </div>
        <p className="relative text-xs text-white/70">Copyright IBM · 77 571 93 98</p>
      </div>
      <div className="grain flex flex-1 items-center justify-center p-6">
        <form onSubmit={submit} className="rise w-full max-w-sm space-y-5 rounded-lg border bg-card p-8 shadow-sm" data-testid="login-form">
          <div><h1 className="font-display text-3xl font-semibold">Connexion</h1><p className="text-sm text-muted-foreground">Accédez à votre espace</p></div>
          <Field label="Nom d'utilisateur"><Input type="text" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" /></Field>
          <Field label="Mot de passe"><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" /></Field>
          {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">{error}</p>}
          <Button type="submit" className="w-full bg-[#059669] hover:bg-[#047857]" disabled={busy} data-testid="login-submit">{busy ? "Connexion…" : "Se connecter"}</Button>
        </form>
      </div>
    </div>
  );
}
